import React, { useState, useCallback, useEffect } from 'react';
import { TextField, CircularProgress } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import LinkIcon from '@material-ui/icons/Link';
import DescriptionIcon from '@material-ui/icons/Description';
import { useApi } from '@backstage/core-plugin-api';
import { useDebounce } from 'react-use';
import { homepageLinksApiRef } from '../../api';
import { AutocompleteResult } from '../../types';

interface LinkAutocompleteProps {
  value: string;
  onChange: (url: string, result?: AutocompleteResult) => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
}

export const LinkAutocomplete = ({
  value,
  onChange,
  label = 'URL',
  placeholder = 'Enter URL or search existing links',
  error,
  helperText,
}: LinkAutocompleteProps) => {
  const api = useApi(homepageLinksApiRef);
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState<AutocompleteResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Sync inputValue when value prop changes (e.g., from drag and drop)
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useDebounce(
    () => {
      setDebouncedQuery(inputValue);
    },
    300,
    [inputValue],
  );

  React.useEffect(() => {
    if (debouncedQuery.length < 2) {
      setOptions([]);
      return;
    }

    let active = true;
    setLoading(true);

    api
      .searchLinks(debouncedQuery)
      .then(results => {
        if (active) {
          setOptions(results);
        }
      })
      .catch(() => {
        if (active) {
          setOptions([]);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [debouncedQuery, api]);

  const handleInputChange = useCallback(
    (_event: React.ChangeEvent<{}>, newInputValue: string) => {
      setInputValue(newInputValue);
      onChange(newInputValue);
    },
    [onChange],
  );

  const handleChange = useCallback(
    (
      _event: React.ChangeEvent<{}>,
      newValue: AutocompleteResult | string | null,
    ) => {
      if (typeof newValue === 'string') {
        onChange(newValue);
      } else if (newValue) {
        onChange(newValue.url, newValue);
      }
    },
    [onChange],
  );

  return (
    <Autocomplete
      freeSolo
      options={options}
      loading={loading}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      getOptionLabel={option =>
        typeof option === 'string' ? option : option.url
      }
      renderOption={option => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {option.type === 'existing' ? (
            <LinkIcon fontSize="small" color="action" />
          ) : (
            <DescriptionIcon fontSize="small" color="action" />
          )}
          <div>
            <div style={{ fontWeight: 500 }}>{option.title}</div>
            <div style={{ fontSize: '0.85em', color: '#666' }}>{option.url}</div>
          </div>
        </div>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          fullWidth
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
